import IconButton from "@mui/material/IconButton";
import React, { useCallback, memo, lazy, Suspense } from "react";
import { AppBar, Toolbar, Typography, alpha, useTheme } from "@mui/material";

import { Outlet, useLocation } from "react-router-dom";

import ErrorBoundary from "./components/ErrorBoundary";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Divider from "@mui/material/Divider";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import logo from "./assets/logo.webp";

import NotificationsIcon from "@mui/icons-material/Notifications";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Collapse } from "@mui/material";

import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Breadcrumbs,
  Link,
} from "@mui/material";

import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

import menuItems from "./pages/components/MenuListItems";
import AuthServices from "./services/AuthServices";
import { CircularProgress } from "@mui/material";

// Lazy load notification/Firebase so main layout paints faster
const NotificationComponent = lazy(() => import("./pages/components/NotificationComponent"));

const DRAWER_WIDTH = 280;

const Layout = () => {
  const theme = useTheme();
  const location = useLocation();
  // const isXLargeScreen = useMediaQuery('(min-width: 1920px)'); // Removed unused variable
  // const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [userProfile, setUserProfile] = React.useState({
    name: "Admin",
    mobile: "",
    email: "",
    profilePicture: logo,
    isVerified: true,
  });

  const handleOpenUserMenu = useCallback((event) => {
    setAnchorElUser(event.currentTarget);
  }, []);

  const handleCloseUserMenu = useCallback(() => {
    setAnchorElUser(null);
  }, []);

  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    const confirmLogout = window.confirm("Are you sure to Logout");
    if (confirmLogout) {
      let data = await AuthServices.logout();
      if (data.status === true) {
        navigate("/login");
      } else {
        console.error("Logout error", data);
      }
    }
  }, [navigate]);
  
  //Add HandleProfile and HandleSettings
  const handleProfile = useCallback(() => {
    navigate("/main/profile");
  }, [navigate]);
  
  const handleSettings = useCallback(() => {
    navigate("/settings");
  }, [navigate]);

  const settings = [
    { label: "Profile", icon: <PersonIcon fontSize="small" />, action: handleProfile },
    { label: "Settings", icon: <SettingsIcon fontSize="small" />, action: handleSettings },
    { label: "Logout", icon: <LogoutIcon fontSize="small" />, action: handleLogout },
  ];

  React.useEffect(() => {
    const fetchUserDetails = () => {
      try {
        const userData = AuthServices.getUserDetails();
        if (userData) {
          // Parse JSON string if it's a string
          const parsedUser = typeof userData === 'string' ? JSON.parse(userData) : userData;
          
          setUserProfile({
            name: parsedUser.name || parsedUser.username || "Admin",
            mobile: parsedUser.mobile || parsedUser.phone || "",
            email: parsedUser.email || "",
            profilePicture: parsedUser.profilePicture || parsedUser.profilepicture || logo,
            isVerified: parsedUser.isVerified || parsedUser.isverified || true,
          });
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        // Keep default values if error occurs
      }
    };

    fetchUserDetails();
  }, []);

  // Drawer open by default on desktop/laptop, can be toggled
  const [open, setOpen] = React.useState(true);

  const handleListItemClick = (path) => {
    navigate(path);
    handleDrawerClose();
  };

  const handleDrawerToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const [openSubMenu, setOpenSubMenu] = React.useState(null);

  const handleSubMenuClick = (index) => {
    if (openSubMenu === index) {
      setOpenSubMenu(null);
    } else {
      setOpenSubMenu(index);
    }
  };

  const handleSubMenuItemClick = (path) => {
    navigate(path);
    handleDrawerClose();
  };

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  // Breadcrumb path mapping - memoized for performance
  const breadcrumbs = React.useMemo(() => {
    const path = location.pathname;
    const pathSegments = path.split("/").filter(Boolean);
    
    // Map of all actual page routes (not parent categories) - static Set for O(1) lookup
    const actualRoutes = new Set([
      "/main/dashboard",
      "/main/profile",
      "/main/preregistered",
      "/main/bookings/all",
      "/main/bookings/live",
      "/main/bookings/pending",
      "/main/bookings/upcoming",
      "/main/bookings/endedtrips",
      "/main/bookings/rejected",
      "/main/bookings/cancelled",
      "/main/bookings/failed",
      "/main/bookings/history",
      "/main/bookings/completetrip",
      "/main/bookings/hosttimings",
      "/main/bookings/totalearnings",
      "/main/customers/all",
      "/main/customers/blocked",
      "/main/host/approve",
      "/main/host/vehiclepricing",
      "/main/host/details",
      "/main/host/blocked",
      "/main/host/hostpayouts",
      "/main/host/offers",
      "/main/agent/all",
      "/main/agent/payments",
      "/main/vehicles/pause",
      "/main/vehicles/list",
      "/main/vehicles/verify",
      "/main/vehicles/approval",
      "/main/vehicles/assigngps",
      "/main/vehicles/tracking",
      "/main/vehicles/blocked",
      "/main/payments/totalearnings",
      "/main/payments/hostearnings",
      "/main/payments/refunds",
      "/main/support/livechat",
      "/main/support/mechanic",
      "/main/complaints",
      "/main/vadmin/vehiclemake",
      "/main/vadmin/vehiclemodel",
      "/main/vadmin/vehiclecategory",
      "/main/vadmin/vehiclefueltype",
      "/main/vadmin/vehiclefeatures",
      "/main/vadmin/vehicletransmission",
      "/main/aadmin/coupons",
      "/main/aadmin/referral",
      "/main/aadmin/protectionplans",
    ]);
    
    const breadcrumbMap = {
      main: "Main",
      dashboard: "Dashboard",
      profile: "Profile",
      preregistered: "Pre-Registered Users",
      live: "Ongoing Bookings",
      pending: "Pending Bookings",
      upcoming: "Upcoming Bookings",
      endedtrips: "Ended Trips",
      rejected: "Rejected",
      cancelled: "Cancelled",
      failed: "Failed",
      history: "History",
      completetrip: "Complete Trip",
      hosttimings: "Host Timings",
      totalearnings: "Total Earnings",
      blocked: "Blocked Customers",
      approve: "Approve Hosts",
      vehiclepricing: "Vehicle Pricing",
      details: "Host Details",
      hostpayouts: "Host Payouts",
      offers: "Host Offers",
      payments: "Payments",
      hostearnings: "Host Earnings",
      refunds: "Refunds",
      pause: "Pause Vehicle",
      list: "Live Vehicles",
      verify: "Verify Vehicles",
      approval: "Vehicle Approval",
      assigngps: "Assign GPS",
      tracking: "Tracking",
      livechat: "Live Chat",
      mechanic: "Mechanic",
      complaints: "Complaints",
      vehiclemake: "Vehicle Make",
      vehiclemodel: "Vehicle Model",
      vehiclecategory: "Vehicle Category",
      vehiclefueltype: "Vehicle Fuel Type",
      vehiclefeatures: "Vehicle Features",
      vehicletransmission: "Vehicle Transmission",
      coupons: "Coupons",
      referral: "Referral System",
      protectionplans: "Protection Plans",
    };

    const result = [
      { label: "Dashboard", path: "/main/dashboard" },
    ];

    if (pathSegments.length > 1) {
      // Build the full path and only include segments that correspond to actual routes
      let currentPath = "/main";
      pathSegments.forEach((segment, index) => {
        if (index === 0 && segment === "main") {
          return; // Skip "main" as it's the base, but keep it in currentPath
        }
        currentPath += `/${segment}`;
        
        // Only add to breadcrumbs if this path is an actual route (not just a parent category)
        if (actualRoutes.has(currentPath)) {
          let label = breadcrumbMap[segment];
          
          // Handle duplicate 'all' key by checking parent segment
          if (!label && segment === 'all') {
            const parentSegment = pathSegments[index - 1];
            if (parentSegment === 'bookings') {
              label = "All Bookings";
            } else if (parentSegment === 'customers') {
              label = "All Customers";
            }
          }
          
          // Fallback to capitalized segment name
          if (!label) {
            label = segment.charAt(0).toUpperCase() + segment.slice(1);
          }
          
          result.push({ label, path: currentPath });
        }
      });
    }

    return result;
  }, [location.pathname]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "grey.50", backgroundColor: "#f5f5f5" }}>
      <Suspense fallback={null}>
        <NotificationComponent />
      </Suspense>
      
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              color: "white",
              mr: 2,
              "&:hover": {
                bgcolor: alpha("#fff", 0.1),
                transform: "scale(1.1)",
              },
              transition: "all 0.2s ease",
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                "&:hover": {
                  opacity: 0.9,
                },
                transition: "opacity 0.2s ease",
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Bharat Self Drive Cars Logo"
                loading="eager"
                decoding="async"
                sx={{
                  height: 42,
                  width: "auto",
                  borderRadius: 1,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                }}
              />
                      <Typography
                        variant="h6"
                        sx={{
                          color: "white",
                          fontWeight: 700,
                          fontSize: { xs: "1rem", sm: "1.25rem", lg: "1.5rem", xl: "1.75rem" },
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Bharat Self Drive Cars
                      </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Notifications">
              <IconButton
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: alpha("#fff", 0.1),
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <Badge badgeContent={1} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Account settings">
              <IconButton
                onClick={handleOpenUserMenu}
                sx={{
                  p: 0.5,
                  border: "2px solid",
                  borderColor: alpha("#fff", 0.3),
                  "&:hover": {
                    borderColor: alpha("#fff", 0.6),
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <Avatar
                  alt={userProfile.name}
                  src={userProfile.profilePicture}
                  sx={{
                    width: 36,
                    height: 36,
                    border: "2px solid white",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          <Menu
            sx={{
              mt: "50px",
              "& .MuiPaper": {
                borderRadius: 2,
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                minWidth: 200,
                mt: 1.5,
              },
            }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {settings.map((setting, index) => (
              <React.Fragment key={setting.label}>
                {index === settings.length - 1 && (
                  <Divider sx={{ my: 0.5 }} />
                )}
                <MenuItem
                  onClick={() => {
                    handleCloseUserMenu();
                    setting.action();
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    gap: 1.5,
                    color: setting.label === "Logout" ? "error.main" : "text.primary",
                    "&:hover": {
                      bgcolor:
                        setting.label === "Logout"
                          ? alpha(theme.palette.error.main, 0.08)
                          : alpha(theme.palette.primary.main, 0.08),
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <Box
                    sx={{
                      color: setting.label === "Logout" ? "error.main" : "text.secondary",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {setting.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: setting.label === "Logout" ? 600 : 500,
                    }}
                  >
                    {setting.label}
                  </Typography>
                </MenuItem>
              </React.Fragment>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRight: "none",
            background: "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "4px 0 24px rgba(0,0,0,0.1)",
            top: "64px", // Start below the AppBar
            height: "calc(100% - 64px)", // Full height minus AppBar
          },
        }}
      >
        {/* User Profile Section */}
        <Box
          sx={{
            p: 1.5,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              top: -30,
              right: -30,
              width: "120px",
              height: "120px",
              background: alpha("#fff", 0.1),
              borderRadius: "50%",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", position: "relative", zIndex: 1 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              color={userProfile.isVerified ? "success" : "error"}
              sx={{
                "& .MuiBadge-dot": {
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  border: "1.5px solid white",
                },
              }}
            >
              <Avatar
                alt={userProfile.name}
                src={userProfile.profilePicture}
                sx={{
                  width: 48,
                  height: 48,
                  border: "2px solid white",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              />
            </Badge>
            <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "white",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  }}
                >
                  {userProfile.name}
                </Typography>
                {userProfile.isVerified && (
                  <VerifiedUserIcon sx={{ fontSize: 14, color: "#4caf50", flexShrink: 0 }} />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: alpha("#fff", 0.9),
                  fontSize: "0.7rem",
                  mb: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  lineHeight: 1.3,
                }}
              >
                {userProfile.mobile}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha("#fff", 0.8),
                  fontSize: "0.7rem",
                  mb: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  lineHeight: 1.3,
                }}
              >
                {userProfile.email}
              </Typography>
              <Chip
                icon={userProfile.isVerified ? <VerifiedUserIcon sx={{ fontSize: 12 }} /> : null}
                label={userProfile.isVerified ? "Verified" : "Not Verified"}
                size="small"
                sx={{
                  bgcolor: userProfile.isVerified
                    ? alpha("#4caf50", 0.2)
                    : alpha("#f44336", 0.2),
                  color: "white",
                  border: "1px solid",
                  borderColor: userProfile.isVerified ? "#4caf50" : "#f44336",
                  fontSize: "0.65rem",
                  height: 18,
                  "& .MuiChip-icon": {
                    color: userProfile.isVerified ? "#4caf50" : "#f44336",
                    marginLeft: "4px",
                  },
                  "& .MuiChip-label": {
                    padding: "0 6px",
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderWidth: 1 }} />

        {/* Menu Items */}
        <List sx={{ px: 1.5, py: 2 }}>
          {menuItems &&
            Array.isArray(menuItems) &&
            menuItems.map((item, index) => {
              const isActive = isActiveRoute(item.path);
              const hasSubMenu = item.subMenuItems && item.subMenuItems.length > 0;
              const isSubMenuOpen = openSubMenu === index;

              return (
                <Box key={index}>
                  <ListItem
                    button
                    onClick={() =>
                      hasSubMenu
                        ? handleSubMenuClick(index)
                        : handleListItemClick(item.path)
                    }
                    sx={{
                      mb: 0.5,
                      borderRadius: 2,
                      bgcolor: isActive
                        ? alpha(theme.palette.primary.main, 0.12)
                        : "transparent",
                      color: isActive ? theme.palette.primary.main : "text.primary",
                      "&:hover": {
                        bgcolor: isActive
                          ? alpha(theme.palette.primary.main, 0.16)
                          : alpha(theme.palette.primary.main, 0.08),
                        transform: "translateX(4px)",
                      },
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                      "&::before": isActive
                        ? {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: "4px",
                            height: "60%",
                            background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
                            borderRadius: "0 4px 4px 0",
                          }
                        : {},
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive
                          ? theme.palette.primary.main
                          : "text.secondary",
                        minWidth: 40,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 700 : 500,
                        fontSize: "0.95rem",
                      }}
                    />
                    {hasSubMenu && (
                      <Box
                        sx={{
                          color: isActive
                            ? theme.palette.primary.main
                            : "text.secondary",
                          transition: "transform 0.2s ease",
                          transform: isSubMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      >
                        {isSubMenuOpen ? <ExpandLess /> : <ExpandMore />}
                      </Box>
                    )}
                  </ListItem>

                  {hasSubMenu && (
                    <Collapse
                      in={isSubMenuOpen}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {item.subMenuItems.map((subItem, subIndex) => {
                          const isSubActive = isActiveRoute(subItem.path);
                          return (
                            <ListItem
                              button
                              key={subIndex}
                              onClick={() => handleSubMenuItemClick(subItem.path)}
                              sx={{
                                pl: 5,
                                mb: 0.5,
                                borderRadius: 2,
                                bgcolor: isSubActive
                                  ? alpha(theme.palette.primary.main, 0.1)
                                  : "transparent",
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  transform: "translateX(4px)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  color: isSubActive
                                    ? theme.palette.primary.main
                                    : "text.secondary",
                                  minWidth: 36,
                                }}
                              >
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.text}
                                primaryTypographyProps={{
                                  fontWeight: isSubActive ? 600 : 400,
                                  fontSize: "0.875rem",
                                  color: isSubActive
                                    ? theme.palette.primary.main
                                    : "text.primary",
                                }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          mt: "64px",
          minHeight: "calc(100vh - 64px)",
          height: "calc(100vh - 64px)",
          backgroundColor: "#f5f5f5",
          maxWidth: "100%",
          overflow: "auto",
          px: { xs: 1, sm: 2, md: 3, lg: 4, xl: 5 },
        }}
      >
        {/* Breadcrumbs */}
        {location.pathname !== "/main/dashboard" && breadcrumbs.length > 1 && (
          <Box
            sx={{
              pt: 2,
              pb: 1.5,
              px: { xs: 1, sm: 2, md: 3, lg: 4, xl: 5 },
              mx: { xs: -1, sm: -2, md: -3, lg: -4, xl: -5 },
              backgroundColor: "white",
              borderBottom: "1px solid",
              borderColor: "divider",
              minHeight: "48px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" sx={{ color: "text.secondary" }} />}
              aria-label="breadcrumb"
              sx={{
                "& .MuiBreadcrumbs-ol": {
                  flexWrap: "nowrap",
                  alignItems: "center",
                },
                "& .MuiBreadcrumbs-li": {
                  maxWidth: "250px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                },
                color: "text.primary",
              }}
            >
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return isLast ? (
                  <Typography
                    key={crumb.path}
                    color="text.primary"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {crumb.label}
                  </Typography>
                ) : (
                  <Link
                    key={crumb.path}
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(crumb.path);
                    }}
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      cursor: "pointer",
                      backgroundColor: "transparent",
                      border: "none",
                      padding: 0,
                      fontFamily: "inherit",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "primary.dark",
                      },
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {index === 0 && <HomeIcon fontSize="small" sx={{ fontSize: "1rem" }} />}
                    {crumb.label}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>
        )}
        <Suspense fallback={
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 'calc(100vh - 64px)',
              backgroundColor: "#f5f5f5",
            }}
          >
            <CircularProgress />
          </Box>
        }>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </Suspense>
      </Box>
    </Box>
  );
};

// Memoize Layout to prevent unnecessary re-renders
export default memo(Layout);
